import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, Phone, ChevronRight, Clock, AlertTriangle, CalendarClock, Timer,
  Scissors, ShoppingCart, Beef, Pill, Coffee, UtensilsCrossed,
  Shirt, Smartphone, BookOpen, Leaf, Store, Gem
} from 'lucide-react';
import { useLocation } from '../../contexts/LocationContext';
import StatusExplainer from '../common/StatusExplainer';

/* Icones par type */
const COMMERCE_ICONS = {
  boulangerie:    { icon: Store,            color: '#d97706' },
  epicerie:       { icon: ShoppingCart,     color: '#2d3f8f' },
  boucherie:      { icon: Beef,             color: '#dc2626' },
  pharmacie:      { icon: Pill,             color: '#059669' },
  cafe:           { icon: Coffee,           color: '#78350f' },
  restaurant:     { icon: UtensilsCrossed,  color: '#7c3aed' },
  coiffeur:       { icon: Scissors,         color: '#db2777' },
  vetements:      { icon: Shirt,            color: '#0891b2' },
  electronique:   { icon: Smartphone,       color: '#1b2a6b' },
  librairie:      { icon: BookOpen,         color: '#b45309' },
  fruits_legumes: { icon: Leaf,             color: '#16a34a' },
  bijouterie:     { icon: Gem,              color: '#2976b6' },
  autre:          { icon: Store,            color: '#6b7280' },
};

const formatDist = d => d < 1 ? `${(d*1000).toFixed(0)}m` : `${d.toFixed(1)}km`;

const timeSince = ts => {
  if (!ts) return null;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return null;
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1)  return "À l'instant";
  if (m < 60) return `-${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `-${h}h`;
  return `-${Math.floor(h/24)}j`;
};

/* ── helper compte à rebours ─────────────────── */
const formatCountdown = (ts) => {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return null;
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return null;
    const totalMin = Math.ceil(diff / 60000);
    if (totalMin < 60) return `${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  } catch { return null; }
};

const STATUS = {
  open:      { label: 'Ouvert',            bg: 'linear-gradient(135deg,#00c9a7,#009e85)', pulse: true  },
  closed:    { label: 'Fermé',             bg: 'linear-gradient(135deg,#f0564a,#dc2626)', pulse: false },
  uncertain: { label: 'Incertain',         bg: 'linear-gradient(135deg,#f5a623,#e8920f)', pulse: true  },
  // be_back : bleu si pas expiré, amber si expiré
  be_back:   { label: 'De retour bientôt', bg: 'linear-gradient(135deg,#60a5fa,#2563eb)', pulse: true  },
};

/* ════════════════════════════════════════════════════════════════ */
const ShopCard = ({ shop }) => {
  const navigate = useNavigate();
  const { location, calculateDistance } = useLocation();
  const [countdown, setCountdown] = useState(null);
  const [beBackExpired, setBeBackExpired] = useState(false);

  /* ── Compte à rebours live ────────────────────── */
  useEffect(() => {
    if (shop.status !== 'be_back' || !shop.returnAt) {
      setCountdown(null);
      setBeBackExpired(false);
      return;
    }
    const tick = () => {
      const cd = formatCountdown(shop.returnAt);
      setCountdown(cd);
      setBeBackExpired(!cd);
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [shop.status, shop.returnAt]);

  const dist   = location ? calculateDistance(location.lat, location.lng, shop.latitude, shop.longitude) : null;

  // Si be_back expiré, afficher comme "uncertain"
  const effectiveStatus = (shop.status === 'be_back' && beBackExpired) ? 'uncertain' : shop.status;
  const status = STATUS[effectiveStatus] || STATUS.closed;

  // Label ajusté pour be_back expiré
  const displayLabel = (shop.status === 'be_back' && beBackExpired)
    ? 'Censé revenir'
    : status.label;

  const { icon: Icon, color: iColor } = COMMERCE_ICONS[shop.commerceType] || COMMERCE_ICONS.autre;
  const isSchedule = shop.managedBy === 'schedule';
  const updatedAt  = timeSince(shop.lastSeenAt || shop.updatedAt);

  const isOld = () => {
    if (!shop.updatedAt || shop.status !== 'open') return false;
    const d = shop.updatedAt?.toDate ? shop.updatedAt.toDate() : new Date(shop.updatedAt);
    return (Date.now() - d.getTime()) / 3600000 > 2;
  };

  return (
    <div
      className="shop-card"
      onClick={() => navigate(`/shop/${shop.id}`)}
      role="button"
      tabIndex={0}
      aria-label={`${shop.name} — ${displayLabel}`}
      onKeyPress={e => e.key === 'Enter' && navigate(`/shop/${shop.id}`)}
    >
      {/* Bande statut latérale */}
      <div className={`shop-card-accent ${effectiveStatus || 'closed'}`}/>

      <div style={{ padding: '0.75rem 0.875rem' }}>

        {/* ═══ HEADER : Icon + Nom/Type + Badge Statut ═══ */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.625rem',
          marginBottom: '0.5rem'
        }}>
          {/* Icone commerce compacte */}
          <div style={{
            width: 36, 
            height: 36, 
            borderRadius: 'var(--r-sm)',
            background: 'var(--bg)',
            boxShadow: 'var(--neu-sm)',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexShrink: 0,
          }}>
            <Icon size={16} color={iColor} strokeWidth={2}/>
          </div>

          {/* Nom + Type empilés */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '0.9375rem',
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'var(--text-1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {shop.name}
            </h3>
            {shop.commerceType && (
              <span style={{ 
                fontSize: '0.625rem', 
                fontWeight: 600, 
                color: 'var(--text-3)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.4px',
                lineHeight: 1.3,
                display: 'block',
              }}>
                {shop.commerceType.replace('_', ' ')}
              </span>
            )}
          </div>

          {/* Badge statut compact */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.25rem',
            flexShrink: 0 
          }}>
            <div style={{
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.25rem',
              padding: '0.25rem 0.5rem', 
              borderRadius: 'var(--r-full)',
              background: status.bg,
            }}>
              <span style={{
                width: 5, 
                height: 5, 
                borderRadius: '50%',
                background: 'rgba(255,255,255,.9)', 
                flexShrink: 0,
                animation: status.pulse ? 'pulseDot 2.2s ease infinite' : 'none',
              }}/>
              <span style={{ 
                fontSize: '0.6875rem', 
                fontWeight: 700, 
                color: '#fff',
                letterSpacing: '-0.2px',
              }}>
                {displayLabel}
              </span>
            </div>
            <ChevronRight size={14} style={{ color: 'var(--text-3)', flexShrink: 0 }}/>
          </div>
        </div>

        {/* ═══ MÉTA-DONNÉES ═══ */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '0.375rem', 
          alignItems: 'center',
          marginBottom: (shop.status === 'uncertain' || shop.status === 'be_back' || isOld() || shop.mightBeForgotten) ? '0.5rem' : 0,
        }}>
          {/* Distance */}
          {dist !== null && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.2rem 0.5rem',
              borderRadius: 'var(--r-sm)',
              background: 'var(--bg-elevated)',
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--text-2)',
            }}>
              <MapPin size={10} strokeWidth={2.5}/>{formatDist(dist)}
            </span>
          )}
          
          {/* Téléphone */}
          {shop.phone && shop.showPhone !== false && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.2rem 0.5rem',
              borderRadius: 'var(--r-sm)',
              background: 'var(--bg-elevated)',
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--text-2)',
            }}>
              <Phone size={10} strokeWidth={2.5}/>{shop.phone}
            </span>
          )}
          
          {/* Auto horaires */}
          {isSchedule && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.2rem 0.5rem',
              borderRadius: 'var(--r-sm)',
              background: 'var(--bg-elevated)',
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--text-2)',
            }}>
              <CalendarClock size={10} strokeWidth={2}/>Horaires auto
            </span>
          )}
          
          {/* Dernière maj */}
          {updatedAt && shop.status === 'open' && (
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.2rem', 
              marginLeft: 'auto',
              fontSize: '0.625rem', 
              fontWeight: 500, 
              color: 'var(--text-3)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <Clock size={9} strokeWidth={2}/>{updatedAt}
            </span>
          )}
        </div>

        {/* ═══ ALERTES ═══ */}

        {/* ── Badge "De retour bientôt" (be_back non expiré) ── */}
        {shop.status === 'be_back' && !beBackExpired && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.5rem 0.625rem',
            borderRadius: 'var(--r-sm)',
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.25)',
            fontSize: '0.75rem', 
            fontWeight: 600, 
            color: '#2563eb',
            lineHeight: 1.4,
          }}>
            <Timer size={10} style={{ flexShrink: 0 }} strokeWidth={2} color="#3b82f6"/>
            <span>
              
              {countdown && (
                <span style={{ fontWeight: 500, color: '#3b82f6', marginLeft: '0.2rem' }}>
                  Retour estimé dans {countdown}
                </span>
              )}
            </span>
            <StatusExplainer type="be_back" size="sm" inline />
          </div>
        )}

        {/* ── Badge "be_back" expiré → incertain ── */}
        {shop.status === 'be_back' && beBackExpired && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.375rem',
            padding: '0.5rem 0.625rem',
            borderRadius: 'var(--r-sm)',
            background: 'var(--warning-bg, rgba(245, 166, 35, 0.08))',
            border: '1px solid var(--warning-border, rgba(245, 166, 35, 0.2))',
            fontSize: '0.75rem', 
            fontWeight: 500, 
            color: 'var(--warning)',
            lineHeight: 1.4,
          }}>
            <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} strokeWidth={2}/>
            <span>Délai dépassé</span>
          </div>
        )}

        {/* Statut incertain */}
        {shop.status === 'uncertain' && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.375rem',
            padding: '0.5rem 0.625rem',
            borderRadius: 'var(--r-sm)',
            background: 'var(--warning-bg, rgba(245, 166, 35, 0.08))',
            border: '1px solid var(--warning-border, rgba(245, 166, 35, 0.2))',
            fontSize: '0.75rem', 
            fontWeight: 500, 
            color: 'var(--warning)',
            lineHeight: 1.4,
          }}>
            <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} strokeWidth={2}/>
            <span>Statut non confirmé récemment</span>
            <StatusExplainer type="uncertain" size="sm" inline />
          </div>
        )}

        {/* Info ancienne (+2h) */}
        {isOld() && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            marginTop: shop.status === 'uncertain' ? '0.375rem' : 0,
            padding: '0.25rem 0.625rem',
            borderRadius: 'var(--r-sm)',
            background: 'var(--warning-bg, rgba(245, 166, 35, 0.08))',
            border: '1px solid var(--warning-border, rgba(245, 166, 35, 0.2))',
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'var(--warning)',
          }}>
            <AlertTriangle size={10} strokeWidth={2.5}/>
            Info non confirmée (+2h)
          </div>
        )}

        {/* Peut-être oublié */}
        {shop.mightBeForgotten && shop.status === 'closed' && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            marginTop: (shop.status === 'uncertain' || isOld()) ? '0.375rem' : 0,
            padding: '0.25rem 0.625rem',
            borderRadius: 'var(--r-sm)',
            background: 'rgba(59, 130, 246, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: '#3b82f6',
          }}>
            <Clock size={10} strokeWidth={2.5}/>
            En théorie ouvert selon les horaires 
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopCard;